const UNLOCK_CAPABILITIES = new Set([
  'unlock', 'door_unlock', 'remote_unlock', 'lock_control', 'access_control',
  'pulse_relay', 'open_door', 'open_gate',
]);

const BLOCKED_TYPES = /camera|siren|speaker|audio|sensor|light|thermostat|intercom|kiosk/;
const DOOR_TYPES = /(^|[_\s-])(door|lock|reader|strike|gate|turnstile|entry|entrance|portal)([_\s-]|$)/;

const strings = (value: unknown): string[] => Array.isArray(value)
  ? value.map(String).map((entry) => entry.trim().toLowerCase()).filter(Boolean)
  : [];

export function deviceCapabilityList(device: Record<string, any>): string[] {
  return Array.from(new Set([
    ...strings(device.capabilities),
    ...strings(device.features),
    ...strings(device.supported_actions),
    ...strings(device.operations),
    ...strings(device.state?.capabilities),
    ...strings(device.raw?.capabilities),
    ...strings(device.__intrlock?.capabilities),
  ]));
}

/** Only return true when the record has an unlock operation or is door-like. */
export function isUnlockCapableDevice(device: Record<string, any> | null | undefined): boolean {
  if (!device) return false;
  const capabilities = deviceCapabilityList(device);
  if (capabilities.some((value) => UNLOCK_CAPABILITIES.has(value))) return true;

  const type = [
    device.canonical_device_class,
    device.canonical_type_detected,
    device.device_type,
    device.type,
  ].filter(Boolean).join(' ').trim().toLowerCase();
  if (BLOCKED_TYPES.test(type)) return false;
  // PDK's canonical reader type is camel-cased as `primaryReader`. Once
  // normalized to lowercase it has no separator for the generic reader
  // boundary expression, but it is still a door/lock control.
  if (type.includes('primaryreader')) return true;
  if (DOOR_TYPES.test(type)) return true;

  // A generic relay is eligible only when its purpose is explicitly door-like.
  if (/(^|[_\s-])relay([_\s-]|$)/.test(type)) {
    const purpose = `${device.relay_type || ''} ${device.name || device.device_name || ''}`.toLowerCase();
    return DOOR_TYPES.test(purpose);
  }
  return false;
}

export const eligibleUnlockDevices = <T extends Record<string, any>>(devices: T[]): T[] =>
  devices.filter(isUnlockCapableDevice);

export const deviceAccessId = (device: Record<string, any>): string =>
  String(device.acs_device_record_id || device.uuid || device.id || '');
